/* Independent, scoped feature demonstrations. No product, price or cart state. */
(() => {
  'use strict';
  if (window.SmarketFeatureDemos) {
    window.SmarketFeatureDemos.scan(document);
    return;
  }
  const instances = new Map();
  function init(root) {
    if (instances.has(root)) return;
    const abort = new AbortController();
    const options = { signal: abort.signal };
    const motion = matchMedia('(prefers-reduced-motion: reduce)');
    const buttons = [...root.querySelectorAll('[data-fd-state]')];
    const label = root.querySelector('[data-fd-label]');
    const panel = root.closest('[data-pfx-panel]');
    const cycle = Math.max(4000, Math.min(15000, Number(root.dataset.cycle) || 8000));
    let elapsed = 0, active = false, visible = false, timer = null;
    let paused = root.dataset.autoplay === 'false', previous = 0;
    function paint() {
      root.dataset.active = String(active);
      label.textContent = active ? root.dataset.onLabel : root.dataset.offLabel;
      buttons.forEach(b => b.setAttribute('aria-pressed', String((b.dataset.fdState === 'on') === active)));
    }
    function stop() {
      clearInterval(timer);
      timer = null;
      root.dataset.running = 'false';
    }
    function sync() {
      const shown = visible && !document.hidden && root.getClientRects().length > 0
        && (!panel || panel.classList.contains('is-active'));
      if (!shown || paused || motion.matches) { stop(); paint(); return; }
      root.dataset.running = 'true';
      if (!timer) {
        previous = performance.now();
        timer = setInterval(() => {
          const now = performance.now();
          elapsed = (elapsed + Math.min(now - previous, 300)) % cycle;
          previous = now;
          const next = elapsed >= cycle * 0.4;
          if (next !== active) { active = next; paint(); }
        }, 100);
      }
      paint();
    }
    buttons.forEach(b => b.addEventListener('click', () => {
      // Choosing either state stops the automatic cycle and keeps that state.
      active = b.dataset.fdState === 'on';
      elapsed = active ? cycle * 0.4 : 0;
      paused = true;
      sync();
    }, options));
    const tabs = root.closest('[data-pfx-id]')?.querySelector('.pfx__tabs');
    const sizing = tabs ? new ResizeObserver(() => {
      root.style.setProperty('--fd-panel-height', Math.round(tabs.getBoundingClientRect().height) + 'px');
    }) : null;
    if (sizing) sizing.observe(tabs);
    const observer = new IntersectionObserver(entries => {
      visible = entries[0].isIntersecting && entries[0].intersectionRatio >= 0.2;
      sync();
    }, { threshold: [0, 0.2] });
    observer.observe(root);
    const mutations = new MutationObserver(sync);
    // Tabs and the existing mobile accordion both hide panels with classes.
    if (panel) mutations.observe(panel, { attributes: true, attributeFilter: ['class'] });
    const section = root.closest('.shopify-section');
    if (section) mutations.observe(section, { attributes: true, attributeFilter: ['class'] });
    document.addEventListener('visibilitychange', sync, options);
    window.addEventListener('pagehide', stop, options);
    window.addEventListener('pageshow', () => {
      observer.unobserve(root);
      observer.observe(root);
    }, options);
    motion.addEventListener('change', sync, options);
    root.dataset.ready = 'true';
    paint();
    instances.set(root, () => { stop(); observer.disconnect(); mutations.disconnect(); sizing?.disconnect(); abort.abort(); });
  }
  function scan(scope) {
    if (scope.matches?.('[data-feature-demo]')) init(scope);
    scope.querySelectorAll('[data-feature-demo]').forEach(init);
  }
  window.SmarketFeatureDemos = { scan };
  scan(document);
  document.addEventListener('shopify:section:load', e => scan(e.target));
  document.addEventListener('shopify:section:unload', e => {
    for (const [root, destroy] of instances) {
      if (e.target.contains(root)) { destroy(); instances.delete(root); }
    }
  });
})();
